import React, {
  useCallback, useMemo, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Form, Select, Row, Divider,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import BoardLinkSelector from '../BoardLinkSelector';
import BoardDropdownFieldSelector from '../BoardDropdownFieldSelector';
import SimpleTextInputModal from '../../common/modals/SimpleTextInputModal';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';

import { getBuckets } from '../../buckets/state/buckets.actions';

import { mergeSets, getProperty, safeExtractFromObject } from '../../helpers/helpers';
import { getDynamicAttributeCustomOptions, getDynamicAttributeSubDataType } from '../boards.helpers';

import {
  setBoardFilters,
  updateBoardFilters,
  refreshBoardCards,
  createBoardFilterView,
  updateBoardFilterView,
  deleteBoardFilterView,
} from '../state/boards.actions';

const VALID_DROPDOWN_DATA_TYPES = new Set([
  'Customers',
  'Projects',
  'Users',
  'Equipment',
  'Vendors',
  'Buckets',
  'Contacts',
  'Custom',
  'Forms',
  'Labels',
  'ScheduleCategories',
  'Divisions',
]);

const VALID_TEXT_FIELD_TYPES = new Set([
  'text',
]);

const VALID_TEXT_ATTRIBUTE_TYPES = new Set([
  'city',
]);

// Fields to serialize for filter views
const FILTER_VIEW_FIELDS = [
  { field: 'users', defaultValue: [] },
  { field: 'link', defaultValue: null },
];

export default function BoardCustomFilterTab({
  onClose,
  filterView = null,
  setActiveTab,
  activeFilters = {},
  selectedBoard = {},
}) {
  const {
    id: boardId,
    divisions: boardDivisions,
    cardTypeId,
  } = selectedBoard;

  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.settings.divisions);
  const paidFlags = useSelector((state) => safeExtractFromObject(state, 'settings.company.paidFlags'));
  const users = useSelector((state) => state.users.users);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const projectCustomFieldTemplate = useSelector((state) => state.projects.customFieldTemplate);
  const customerCustomFieldTemplate = useSelector((state) => state.customers.customFieldTemplate);
  const userCustomFieldTemplate = useSelector((state) => state.users.customFieldTemplate);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const bucketTemplateIds = new Set();
    cardTemplates[cardTypeId]?.fields?.forEach(({ fields = [] } = {}) => {
      fields?.forEach((field) => {
        const { selectedType, configProps: { dataType, subDataType } = {} } = field;
        if (selectedType === 'dropdown' && dataType === 'Buckets' && subDataType) {
          bucketTemplateIds.add(subDataType);
        }
      });
    });

    if (bucketTemplateIds.size) {
      dispatch(getBuckets({ bucketTemplateIds: Array.from(bucketTemplateIds) }));
    }
  }, [cardTemplates, cardTypeId]);
  
  const currentFilters = useMemo(() => {
    if (!filterView || activeFilters.filterView === filterView.id) {
      return activeFilters;
    }
    return filterView;
  }, [filterView, activeFilters]);

  const formTitle = useMemo(() => {
    if (filterView) {
      return `Edit ${filterView.filterViewName} View`;
    }
    return 'Custom Filter';
  }, [filterView]);

  const userOptions = useMemo(() => {
    const divSet = new Set(boardDivisions ?? []);
    const userDivSets = mergeSets(
      Object.values(divisions)
        .filter(({ id: divisionId }) => divSet.has(divisionId))
        .map(({ users: divUsers }) => divUsers),
    );
    return users
      .filter((user) => user.active && userDivSets.has(user.id))
      .map((user) => ({
        value: user.id,
        label: user.name,
      }));
  }, [users, divisions, boardDivisions]);

  const relevantCardTemplateFields = useMemo(() => {
    const relevantFields = [];
    cardTemplates[cardTypeId]?.fields?.forEach(({ name: sectionName, fields = [] } = {}) => {
      fields?.forEach((field = {}) => {
        const { id, selectedType, configProps = {} } = field;
        const {
          dataType,
          title: fieldName,
          customOptions: fieldOptions = configProps.options || [],
          fieldDataType,
          fieldId,
          subType,
          subDataType,
          type,
        } = configProps;

        if (selectedType === 'dropdown' && VALID_DROPDOWN_DATA_TYPES.has(dataType)) {
          relevantFields.push({
            id,
            name: `${sectionName} - ${fieldName}`,
            dataType,
            fieldOptions,
            subDataType,
          });
          return;
        }

        if (selectedType === 'dynamicAttribute' && VALID_DROPDOWN_DATA_TYPES.has(fieldDataType)) {
          const customOptions = getDynamicAttributeCustomOptions({
            fieldDataType,
            dataType,
            fieldId,
            subType,
            projectCustomFieldTemplate,
            customerCustomFieldTemplate,
            userCustomFieldTemplate,
            equipmentTypes,
          });
          relevantFields.push({
            id,
            name: `${sectionName} - ${fieldName}`,
            dataType: fieldDataType,
            fieldOptions: customOptions,
            subDataType: subDataType ?? getDynamicAttributeSubDataType({
              dataType,
              fieldId,
              subType,
              projectCustomFieldTemplate,
              customerCustomFieldTemplate,
              userCustomFieldTemplate,
              equipmentTypes,
            }),
          });
          return;
        }

        if (selectedType === 'attribute' && VALID_TEXT_ATTRIBUTE_TYPES.has(type)) {
          relevantFields.push({
            id,
            name: `${sectionName} - ${fieldName}`,
            dataType: 'Text',
            fieldOptions: [],
          });
          return;
        }

        if (VALID_TEXT_FIELD_TYPES.has(selectedType) && paidFlags?.includes('AB_WORK_REPORTING')) {
          relevantFields.push({
            id,
            name: `${sectionName} - ${fieldName}`,
            dataType: 'Text',
            fieldOptions: [],
          });
        }
      });
    });
    return relevantFields;
  }, [
    cardTemplates,
    cardTypeId,
    projectCustomFieldTemplate,
    customerCustomFieldTemplate,
    userCustomFieldTemplate,
    equipmentTypes,
    paidFlags,
  ]);

  // Build filter view fields including dynamic dropdown fields
  const allFilterViewFields = useMemo(() => {
    const fields = [...FILTER_VIEW_FIELDS];
    relevantCardTemplateFields.forEach((field) => {
      fields.push({ field: field.id, defaultValue: [] });
    });
    return fields;
  }, [relevantCardTemplateFields]);

  const textFieldIds = useMemo(() => (
    new Set(relevantCardTemplateFields.filter((f) => f.dataType === 'Text').map((f) => f.id))
  ), [relevantCardTemplateFields]);

  const defaultFormValues = useMemo(() => {
    const resetValues = {
      users: [],
      link: null,
      alwaysShowAssigned: false,
    };
    relevantCardTemplateFields.forEach((field) => {
      resetValues[field.id] = textFieldIds.has(field.id) ? '' : [];
    });
    return resetValues;
  }, [relevantCardTemplateFields, textFieldIds]);

  const resetFields = useCallback(() => {
    form.setFieldsValue(defaultFormValues);
  }, [form, defaultFormValues]);

  // Initialize form with current filters
  useEffect(() => {
    if (form) {
      const formValues = {
        ...defaultFormValues,
        filterViewName: getProperty(currentFilters, 'filterViewName', ''),
        users: getProperty(currentFilters, 'users', []),
        link: getProperty(currentFilters, 'link', null),
        alwaysShowAssigned: getProperty(currentFilters, 'alwaysShowAssigned', false),
      };

      // Set dynamic dropdown field values
      relevantCardTemplateFields.forEach((field) => {
        if (textFieldIds.has(field.id)) {
          const stored = getProperty(currentFilters, field.id, []);
          formValues[field.id] = Array.isArray(stored) ? (stored[0] ?? '') : stored;
        } else {
          formValues[field.id] = getProperty(currentFilters, field.id, []);
        }
      });

      form.setFieldsValue(formValues);
    }

    return () => {
      resetFields();
    };
  }, [defaultFormValues, currentFilters, relevantCardTemplateFields, textFieldIds]);

  const onApply = useCallback(() => {
    const values = form.getFieldsValue();

    textFieldIds.forEach((id) => {
      const raw = values[id];
      values[id] = raw ? [raw] : [];
    });

    if (filterView) {
      values.filterView = filterView.id;
    } else {
      values.filterView = null;
      values.filterViewName = null;
    }

    dispatch(updateBoardFilters(values));
    dispatch(refreshBoardCards(boardId));
    onClose();
  }, [boardId, form, onClose, dispatch, filterView, textFieldIds]);

  const onClear = useCallback(() => {
    form.setFieldsValue(defaultFormValues);
    dispatch(setBoardFilters());
    dispatch(refreshBoardCards(boardId));
    onClose();
  }, [form, defaultFormValues, boardId, onClose, dispatch]);

  const onSave = useCallback(() => {
    const values = form.getFieldsValue();
    if (filterView) {
      dispatch(updateBoardFilterView(filterView.id, {
        name: values.filterViewName,
        alwaysShowAssigned: values.alwaysShowAssigned,
        filters: allFilterViewFields.map(({ field, defaultValue }) => ({
          filterProperty: field,
          filterValue: JSON.stringify(values[field] ?? defaultValue),
          filterViewId: filterView.id,
        })),
      }));
      onApply();
    } else {
      setIsModalOpen(true);
    }
  }, [filterView, allFilterViewFields, onApply, dispatch, form]);

  const onDelete = useCallback(() => new Promise((resolve) => {
    CustomConfirmModal({
      title: 'Delete Filter View',
      content: (
        <p>
          Are you sure you wish to delete this filter view?
        </p>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      async onOk() {
        resolve(await dispatch(deleteBoardFilterView(filterView.id)));

        if (activeFilters.filterView === filterView.id) {
          dispatch(updateBoardFilters({
            filterView: null,
            filterViewName: null,
          }));
        } else {
          setActiveTab(activeFilters.filterView || 'custom');
        }

        setIsModalOpen(false);
      },
      onCancel() {
        resolve();
      },
    });
  }), [activeFilters, filterView, onClose, dispatch, setActiveTab]);

  const addNewFilterView = useCallback(async (filterViewName) => {
    const values = form.getFieldsValue();

    const {
      id,
      filterViewName: newFilterViewName,
    } = await dispatch(createBoardFilterView(boardId, {
      name: filterViewName,
      alwaysShowAssigned: values.alwaysShowAssigned,
      filters: allFilterViewFields.map(({ field, defaultValue }) => ({
        filterProperty: field,
        filterValue: JSON.stringify(values[field] ?? defaultValue),
      })),
    }));

    onApply();
    setActiveTab(id);
    dispatch(updateBoardFilters({
      filterView: id,
      filterViewName: newFilterViewName,
    }));
  }, [boardId, form, allFilterViewFields, onApply, setActiveTab, dispatch]);

  return (
    <>
      <div>
        <Row>
          <h3>{formTitle}</h3>
          <OnTraccrButton
            onClick={resetFields}
            style={{ marginLeft: 'auto' }}
            title="Reset"
          />
        </Row>
        <Form
          form={form}
          layout="vertical"
        >
          {filterView && (
            <>
              <Form.Item
                name="filterViewName"
                label="Filter View Name"
                rules={[
                  { required: true, message: 'Filter View Name is required' },
                ]}
              >
                <OnTraccrTextInput
                  style={{ width: '100%' }}
                  placeholder="Name"
                />
              </Form.Item>
              <Divider />
            </>
          )}
          <Form.Item
            name="users"
            label="Assigned Users"
            style={{ marginBottom: 16 }}
          >
            <Select
              mode="multiple"
              allowClear
              options={userOptions}
              placeholder="Users"
              optionFilterProp="label"
              showSearch
            />
          </Form.Item>
          <Form.Item
            name="alwaysShowAssigned"
            style={{ marginBottom: 16 }}
          >
            <OnTraccrCheckbox label="Always show cards assigned to me" />
          </Form.Item>
          <Form.Item
            name="link"
            label="Link"
            style={{ marginBottom: 16 }}
          >
            <BoardLinkSelector />
          </Form.Item>
          {relevantCardTemplateFields.length > 0 && <Divider />}
          {relevantCardTemplateFields.map(({
            id, name, dataType, fieldOptions, subDataType,
          }) => (
            <Form.Item
              key={id}
              name={id}
              label={name}
              style={{ marginBottom: 16 }}
            >
              {dataType === 'Text' ? (
                <OnTraccrTextInput placeholder="Filter by value" />
              ) : (
                <BoardDropdownFieldSelector
                  dataType={dataType}
                  divisionIds={boardDivisions}
                  users={userOptions}
                  options={fieldOptions}
                  cardTypeId={cardTypeId}
                  subDataType={subDataType}
                />
              )}
            </Form.Item>
          ))}
        </Form>
      </div>
      <div className="drawer-footer">
        <Row justify="end" gutter={10}>
          <div style={{ marginRight: 'auto' }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {filterView && (
              <OnTraccrButton
                title="Delete"
                type="back"
                onClick={onDelete}
                style={{ marginLeft: 8 }}
              />
            )}
            {!filterView && (
              <OnTraccrButton
                onClick={onClear}
                title="Clear"
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
          <OnTraccrButton
            title={`${filterView ? 'Save' : 'Create'} and Apply`}
            onClick={onSave}
            style={{ marginRight: 8 }}
          />
          <OnTraccrButton
            title="Apply"
            onClick={onApply}
          />
        </Row>
      </div>
      <SimpleTextInputModal
        title="Add Filter View"
        visible={isModalOpen}
        placeholder="Enter Filter View name"
        onClose={() => setIsModalOpen(false)}
        onSave={addNewFilterView}
      />
    </>
  );
}

BoardCustomFilterTab.propTypes = {
  onClose: PropTypes.func.isRequired,
  filterView: PropTypes.shape({
    filterViewName: PropTypes.string,
    id: PropTypes.string,
    user: PropTypes.string,
    link: PropTypes.shape({}),
  }),
  setActiveTab: PropTypes.func,
  activeFilters: PropTypes.shape({
    filterView: PropTypes.string,
  }),
  selectedBoard: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    divisions: PropTypes.arrayOf(PropTypes.string),
    cardTypeId: PropTypes.string,
  }),
};

BoardCustomFilterTab.defaultProps = {
  filterView: null,
  setActiveTab: () => {},
  activeFilters: {},
  selectedBoard: {},
};
